import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getUserCredits } from '../services/api';
import { useAuth } from './AuthContext';

const CreditsContext = createContext();

export const useCredits = () => {
  const context = useContext(CreditsContext);
  if (!context) {
    throw new Error('useCredits must be used within a CreditsProvider');
  }
  return context;
};

export const CreditsProvider = ({ children }) => {
  const { user } = useAuth();
  const [credits, setCredits] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Cache duration: 5 minutos
  const CACHE_DURATION = 5 * 60 * 1000;

  const fetchCredits = useCallback(async () => {
    setIsLoading(true);
    try {
      console.log('Fetching fresh credits data...');
      const creditsData = await getUserCredits();
      setCredits(creditsData);
      setLastUpdated(Date.now());
      console.log('Credits fetched successfully:', creditsData);
      return creditsData;
    } catch (error) {
      console.warn('Credits API error:', error);
      // Si falla, usar defaults
      const defaultCredits = {
        credits: {
          available: 0,
          total_purchased: 0
        }
      };
      setCredits(defaultCredits);
      console.log('Using default credits due to API error');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Cargar al cambiar usuario
  useEffect(() => {
    if (user) {
      fetchCredits();
    } else {
      setCredits(null);
      setLastUpdated(null);
      setIsLoading(false);
    }
  }, [user, fetchCredits]);



  // Función para decrementar créditos después de enviar oferta
  const decrementCredits = useCallback((amount = 1) => {
    if (credits && credits.credits.available >= amount) {
      const updatedCredits = {
        credits: {
          available: credits.credits.available - amount,
          total_purchased: credits.credits.total_purchased
        }
      };
      setCredits(updatedCredits);
      setLastUpdated(Date.now());
      console.log('Credits decremented:', updatedCredits);
    }
  }, [credits]);

  const formatCreditsDisplay = useCallback(() => {
    if (isLoading && !credits) {
      return { number: '...', label: 'CARGANDO...' };
    }
    
    if (!credits) {
      return { number: '0/0', label: 'OFERTAS DISPONIBLES' };
    }
    
    return {
      number: `${credits.credits.available}/${credits.credits.total_purchased}`,
      label: 'OFERTAS DISPONIBLES'
    };
  }, [isLoading, credits]);

  const formatCreditsDisplaySidebar = useCallback(() => {
    if (isLoading && !credits) {
      return 'CARGANDO...';
    }
    
    if (!credits) {
      return '0/0 OFERTAS DISPONIBLES';
    }
    
    return `${credits.credits.available}/${credits.credits.total_purchased} OFERTAS DISPONIBLES`;
  }, [isLoading, credits]);

  // Función para refrescar créditos manualmente
  const refreshCredits = useCallback(() => {
    return fetchCredits();
  }, [fetchCredits]);

  return (
    <CreditsContext.Provider value={{
      credits,
      isLoading,
      fetchCredits,
      refreshCredits,
      decrementCredits,
      formatCreditsDisplay,
      formatCreditsDisplaySidebar,
      lastUpdated
    }}>
      {children}
    </CreditsContext.Provider>
  );
}; 